import type { TFunction } from 'i18next';
import FamlaServices from "./FamlaServices";

const MARKETPLACE_ERROR_KEYS: Record<string, string> = {
    TENANT_MISMATCH: 'microsoft.tenant_mismatch',
    REQUIRES_WORKSPACE: 'microsoft.workspace_required',
    FREE_STARTER_ALREADY_CLAIMED: 'microsoft.error.free_starter_already_claimed',
    ACTIVATION_RETRY: 'microsoft.error.activation_retry',
    ACTIVATION_FAILED: 'microsoft.error.activation_failed',
    COMPANY_MISMATCH: 'microsoft.error.company_mismatch',
    TOKEN_EXPIRED: 'microsoft.error.token_expired',
};

export function getMarketplaceErrorCode(
    detail?: MarketplaceErrorDetail | string,
): string | undefined {
    if (typeof detail === 'object' && detail?.code) {
        return detail.code;
    }
    return undefined;
}

export function resolveMarketplaceErrorMessage(
    t: TFunction,
    detail?: MarketplaceErrorDetail | string,
    fallbackKey = 'microsoft.error.activation_failed',
): string {
    const code = getMarketplaceErrorCode(detail);
    if (code && MARKETPLACE_ERROR_KEYS[code]) {
        return t(MARKETPLACE_ERROR_KEYS[code]);
    }
    if (typeof detail === 'string' && detail.trim()) {
        return detail;
    }
    return t(fallbackKey);
}

interface BillingRecommendation {
    recommended_provider: 'stripe' | 'microsoft';
    message?: string;
    marketplace_url?: string;
    allow_stripe: boolean;
    reason: string;
}

export interface MarketplaceErrorDetail {
    code?: string;
    message?: string;
    tenant_id?: string;
    subscription_id?: string;
}

export interface MarketplaceApiError {
    detail?: MarketplaceErrorDetail | string;
    status?: number;
    response?: {
        data?: {
            detail?: MarketplaceErrorDetail | string;
        };
    };
}

export interface MicrosoftActivationResponse {
    status: 'success' | 'already_processed' | string;
    message?: string;
    company_id?: string;
    subscription_id?: string;
    plan_id?: string;
    credits_added?: number;
    new_balance?: number;
    balance?: number;
    redirect_url?: string;
}

class MicrosoftService {
    static async getBillingRecommendation(
        companyId: string,
        accessToken: string
    ): Promise<BillingRecommendation> {
        const response = await FamlaServices(
            "api_host",
            `/billing/recommendation/${companyId}`,
            "GET",
            null,
            accessToken
        );
        return response.body;
    }

    static async activateSubscription(
        token: string,
        accessToken: string
    ): Promise<MicrosoftActivationResponse> {
        const response = await FamlaServices(
            "api_host",
            `/microsoft/saas/resolve?token=${encodeURIComponent(token)}`,
            "POST",
            null,
            accessToken
        );

        if (response.status >= 400) {
            const apiError: MarketplaceApiError = {
                detail: response.body?.detail,
                status: response.status,
            };
            throw apiError;
        }

        return response.body;
    }
}

export default MicrosoftService;

export { MARKETPLACE_ERROR_KEYS };
